/**
 * SentimentAnalysis Component - Social media sentiment, news analysis, and Fear/Greed gauge
 * @version 5.4.0 - Full implementation
 */
import { useState, useEffect, useCallback } from 'react';
import {
  MessageCircle, TrendingUp, TrendingDown, AlertTriangle, RefreshCw,
  Twitter, Globe, Newspaper, BarChart2, Activity, Zap, ThermometerSun,
  Heart, Frown, Meh, Smile, Users, Clock, Hash
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'https://trading-api-1075463475276.us-central1.run.app';

export default function SentimentAnalysis({ symbol = 'BTCUSD', theme = 'dark' }) {
  const [loading, setLoading] = useState(true);
  const [sentiment, setSentiment] = useState(null);
  const [fearGreedIndex, setFearGreedIndex] = useState(null);
  const [socialMetrics, setSocialMetrics] = useState(null);
  const [newsSentiment, setNewsSentiment] = useState(null);
  const [politicalSentiment, setPoliticalSentiment] = useState(null);
  const [vixData, setVixData] = useState(null);
  const [tariffMonitor, setTariffMonitor] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(symbol);
  const [timeRange, setTimeRange] = useState('24h');
  const [lastUpdate, setLastUpdate] = useState(null);

  const popularSymbols = ['BTCUSD', 'ETHUSD', 'NVDA', 'AAPL', 'TSLA', 'MSFT', 'AMD', 'META'];

  useEffect(() => {
    fetchSentimentData();
  }, [selectedSymbol, timeRange]);

  const fetchSentimentData = async () => {
    setLoading(true);
    try {
      // Fetch real data from multiple endpoints in parallel
      const cleanSymbol = selectedSymbol.replace('USD', '').replace('/', '');
      const [alertsRes, newsRes, marketRes] = await Promise.allSettled([
        fetch(`${API_BASE}/api/alerts/symbol/${selectedSymbol}`).then(r => r.json()),
        fetch(`${API_BASE}/api/finnhub/company-news/${cleanSymbol}`).then(r => r.json()),
        fetch(`${API_BASE}/api/alerts/market-summary`).then(r => r.json())
      ]);

      // Process market signals into sentiment
      const alertsData = alertsRes.status === 'fulfilled' ? alertsRes.value : null;
      const signals = alertsData?.signals || alertsData?.data || [];

      // Compute sentiment from real technical indicators
      let bullishCount = 0, bearishCount = 0, neutralCount = 0;
      signals.forEach(sig => {
        const rsi = sig.rsi_14 || sig.rsi || 50;
        const macd = sig.macd || 0;
        const pctChange = sig.price_change_pct || sig.change_pct || 0;
        if (rsi > 50 && macd > 0 && pctChange > 0) bullishCount++;
        else if (rsi < 50 && macd < 0 && pctChange < 0) bearishCount++;
        else neutralCount++;
      });

      const total = Math.max(bullishCount + bearishCount + neutralCount, 1);
      const bullishPct = (bullishCount / total) * 100;
      const bearishPct = (bearishCount / total) * 100;
      const neutralPct = (neutralCount / total) * 100;
      const overallScore = bullishPct / 100;

      setSentiment({
        overall: overallScore || 0.5,
        bullish: bullishPct || 50,
        bearish: bearishPct || 30,
        neutral: neutralPct || 20,
        trend: bullishPct > bearishPct ? 'bullish' : 'bearish',
        change_24h: signals.length > 0 ? (signals[0].price_change_pct || 0).toFixed(2) : '0.00'
      });

      // Compute Fear & Greed from market-wide signals
      const marketData = marketRes.status === 'fulfilled' ? marketRes.value : null;
      const marketSignals = marketData?.signals || marketData?.data || marketData?.summary || [];
      const allSignals = Array.isArray(marketSignals) ? marketSignals : [];

      let fearGreedValue = 50; // default neutral
      if (allSignals.length > 0) {
        // Compute from RSI averages, price changes, volume ratios
        const avgRsi = allSignals.reduce((sum, s) => sum + (s.rsi_14 || s.rsi || 50), 0) / allSignals.length;
        const avgChange = allSignals.reduce((sum, s) => sum + (s.price_change_pct || s.change_pct || 0), 0) / allSignals.length;
        fearGreedValue = Math.max(0, Math.min(100, Math.round(avgRsi * 0.6 + (avgChange + 5) * 4)));
      } else if (signals.length > 0) {
        const avgRsi = signals.reduce((sum, s) => sum + (s.rsi_14 || s.rsi || 50), 0) / signals.length;
        fearGreedValue = Math.max(0, Math.min(100, Math.round(avgRsi)));
      }

      const fgLabel = fearGreedValue <= 25 ? 'Extreme Fear' :
                      fearGreedValue <= 45 ? 'Fear' :
                      fearGreedValue <= 55 ? 'Neutral' :
                      fearGreedValue <= 75 ? 'Greed' : 'Extreme Greed';

      setFearGreedIndex({
        value: fearGreedValue,
        label: fgLabel,
        signalCount: allSignals.length || signals.length || 0
      });

      // Social/signal metrics derived from technical analysis
      const signalCount = signals.length;
      const socialSentimentScore = overallScore || 0.5;
      setSocialMetrics({
        signals: {
          count: signalCount,
          bullish: bullishCount,
          bearish: bearishCount,
          neutral: neutralCount,
          sentiment: socialSentimentScore,
          strongSignal: bullishPct > 65 || bearishPct > 65
        }
      });

      // Fetch political/White House sentiment (non-blocking)
      fetch(`${API_BASE}/api/rise-cycle/political-sentiment?days=7`)
        .then(r => r.json())
        .then(data => {
          if (data.political_news || data.sector_trump_impact) {
            const news = data.political_news || [];
            const avgScore = news.length > 0
              ? news.reduce((sum, n) => sum + (n.sentiment_score || 0), 0) / news.length
              : 0;
            setPoliticalSentiment({
              news: news.slice(0, 5),
              avgScore,
              totalHeadlines: news.length,
              trumpRelated: news.filter(n => n.trump_related).length,
              tariffRelated: news.filter(n => n.tariff_related).length,
              fedRelated: news.filter(n => n.fed_related).length
            });
          }
        })
        .catch(() => {});

      // Fetch VIX data for options sentiment proxy (non-blocking)
      fetch(`${API_BASE}/api/econ360/series/VIXCLS?limit=30`)
        .then(r => r.json())
        .then(data => {
          const points = data.data || data.series || [];
          if (points.length > 0) {
            const latest = points[points.length - 1]?.value || points[0]?.value || 0;
            const prev = points.length > 1 ? (points[points.length - 2]?.value || points[1]?.value || latest) : latest;
            setVixData({
              current: latest,
              previous: prev,
              change: latest - prev,
              trend: points.map(p => p.value || 0),
              interpretation: latest > 30 ? 'High fear — heavy hedging activity' :
                            latest > 20 ? 'Elevated caution — moderate hedging' :
                            latest > 15 ? 'Normal market conditions' : 'Complacency — low volatility'
            });
          }
        })
        .catch(() => {});

      // Fetch extended political data for tariff monitor (non-blocking)
      fetch(`${API_BASE}/api/rise-cycle/political-sentiment?days=30`)
        .then(r => r.json())
        .then(data => {
          const news = data.political_news || [];
          const tariffNews = news.filter(n => n.tariff_related ||
            (n.headline || '').toLowerCase().includes('tariff') ||
            (n.headline || '').toLowerCase().includes('trade war') ||
            (n.headline || '').toLowerCase().includes('import duty'));
          if (tariffNews.length > 0) {
            const sectors = data.sector_trump_impact || [];
            const affectedSectors = sectors.filter(s => s.tariff_exposure > 0 || s.china_exposure > 0);
            setTariffMonitor({
              count: tariffNews.length,
              recentHeadlines: tariffNews.slice(0, 3),
              avgSentiment: tariffNews.reduce((sum, n) => sum + (n.sentiment_score || 0), 0) / tariffNews.length,
              affectedSectors: affectedSectors.slice(0, 5)
            });
          }
        })
        .catch(() => {});

      // Process Finnhub news into sentiment
      const newsData = newsRes.status === 'fulfilled' ? newsRes.value : null;
      const newsArticles = newsData?.articles || newsData?.data || newsData || [];
      const articleList = Array.isArray(newsArticles) ? newsArticles.slice(0, 20) : [];

      let posCount = 0, negCount = 0, neuCount = 0;
      const topStories = articleList.slice(0, 5).map(article => {
        const headline = article.headline || article.title || '';
        const lower = headline.toLowerCase();
        // Simple keyword sentiment analysis
        const posBias = /surge|rally|gain|rise|boost|buy|bull|grow|record|beat|strong|up/i.test(lower);
        const negBias = /crash|fall|drop|plunge|sell|bear|loss|weak|down|fear|risk|warn/i.test(lower);
        const artSentiment = posBias ? 'positive' : negBias ? 'negative' : 'neutral';
        if (artSentiment === 'positive') posCount++;
        else if (artSentiment === 'negative') negCount++;
        else neuCount++;

        const artDate = article.datetime ? new Date(article.datetime * 1000) : new Date();
        const hoursAgo = Math.round((Date.now() - artDate.getTime()) / 3600000);
        return {
          title: headline || `${selectedSymbol} Market Update`,
          source: article.source || 'Market News',
          sentiment: artSentiment,
          time: hoursAgo < 1 ? 'Just now' : `${hoursAgo}h ago`
        };
      });

      setNewsSentiment({
        articles: articleList.length,
        positive: posCount,
        negative: negCount,
        neutral: neuCount,
        topStories: topStories.length > 0 ? topStories : [
          { title: `No recent news available for ${selectedSymbol}`, source: 'System', sentiment: 'neutral', time: 'N/A' }
        ]
      });

      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching sentiment:', err);
      // Set empty state on error
      setSentiment({ overall: 0.5, bullish: 50, bearish: 30, neutral: 20, trend: 'neutral', change_24h: '0.00' });
      setFearGreedIndex({ value: 50, label: 'Neutral', signalCount: 0 });
      setSocialMetrics({ signals: { count: 0, bullish: 0, bearish: 0, neutral: 0, sentiment: 0.5, strongSignal: false } });
      setNewsSentiment({ articles: 0, positive: 0, negative: 0, neutral: 0, topStories: [{ title: 'Unable to load news data', source: 'System', sentiment: 'neutral', time: 'N/A' }] });
    }
    setLoading(false);
  };

  const getSentimentColor = (value) => {
    if (value >= 0.6) return '#10b981';
    if (value >= 0.4) return '#f59e0b';
    return '#ef4444';
  };

  const getFearGreedColor = (value) => {
    if (value <= 25) return '#ef4444';
    if (value <= 45) return '#f97316';
    if (value <= 55) return '#f59e0b';
    if (value <= 75) return '#84cc16';
    return '#10b981';
  };

  const getFearGreedIcon = (value) => {
    if (value <= 25) return Frown;
    if (value <= 45) return Meh;
    if (value <= 55) return Meh;
    if (value <= 75) return Smile;
    return Heart;
  };

  const GaugeChart = ({ value, label }) => {
    const rotation = (value / 100) * 180 - 90;
    const color = getFearGreedColor(value);

    return (
      <div style={{ position: 'relative', width: '280px', height: '160px', margin: '0 auto' }}>
        {/* Background Arc */}
        <svg viewBox="0 0 200 110" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="25%" stopColor="#f97316" />
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="75%" stopColor="#84cc16" />
              <stop offset="100%" stopColor="#10b981" />
            </linearGradient>
          </defs>
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="url(#gaugeGradient)"
            strokeWidth="16"
            strokeLinecap="round"
          />
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="#1e293b"
            strokeWidth="16"
            strokeLinecap="round"
            strokeDasharray="251.2"
            strokeDashoffset={251.2 * (value / 100)}
            style={{ opacity: 0.3 }}
          />
        </svg>

        {/* Needle */}
        <div style={{
          position: 'absolute',
          left: '50%',
          bottom: '10px',
          width: '4px',
          height: '70px',
          background: color,
          transformOrigin: 'bottom center',
          transform: `translateX(-50%) rotate(${rotation}deg)`,
          borderRadius: '2px',
          boxShadow: `0 0 10px ${color}`
        }} />

        {/* Center Circle */}
        <div style={{
          position: 'absolute',
          left: '50%',
          bottom: '0',
          width: '20px',
          height: '20px',
          background: '#0f172a',
          border: `3px solid ${color}`,
          borderRadius: '50%',
          transform: 'translateX(-50%)'
        }} />

        {/* Labels */}
        <div style={{ position: 'absolute', left: '10px', bottom: '-5px', fontSize: '10px', color: '#ef4444' }}>
          Fear
        </div>
        <div style={{ position: 'absolute', right: '10px', bottom: '-5px', fontSize: '10px', color: '#10b981' }}>
          Greed
        </div>
      </div>
    );
  };

  const SentimentBar = ({ label, value, color }) => (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ color: '#94a3b8', fontSize: '13px' }}>{label}</span>
        <span style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>{value.toFixed(1)}%</span>
      </div>
      <div style={{ height: '8px', background: '#1e293b', borderRadius: '4px' }}>
        <div style={{
          width: `${value}%`,
          height: '100%',
          background: color,
          borderRadius: '4px',
          transition: 'width 0.5s ease'
        }} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <RefreshCw size={48} className="spin" style={{ color: '#10b981', marginBottom: '16px' }} />
        <div>Loading sentiment data...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'white', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ThermometerSun size={32} color="#10b981" />
          Sentiment Analysis
        </h1>
        <p style={{ color: '#64748b', marginTop: '4px' }}>
          Real-time market sentiment from social media, news, and on-chain data
        </p>
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        {/* Symbol Selection */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {popularSymbols.map(sym => (
            <button
              key={sym}
              onClick={() => setSelectedSymbol(sym)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: selectedSymbol === sym
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : '#1e293b',
                color: 'white',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: selectedSymbol === sym ? '600' : '400'
              }}
            >
              {sym}
            </button>
          ))}
        </div>

        {/* Time Range */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {['1h', '24h', '7d', '30d'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: timeRange === range ? '#3b82f6' : '#1e293b',
                color: 'white',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              {range}
            </button>
          ))}
          <button
            onClick={fetchSentimentData}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: 'none',
              background: '#1e293b',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Main Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Fear & Greed Index */}
        <div style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid #334155'
        }}>
          <h3 style={{ color: 'white', marginBottom: '20px', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={20} color="#10b981" />
            Fear & Greed Index
          </h3>

          {fearGreedIndex && (
            <>
              <GaugeChart value={fearGreedIndex.value} label={fearGreedIndex.label} />

              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <div style={{ fontSize: '48px', fontWeight: '700', color: getFearGreedColor(fearGreedIndex.value) }}>
                  {fearGreedIndex.value}
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: getFearGreedColor(fearGreedIndex.value),
                  marginBottom: '16px'
                }}>
                  {fearGreedIndex.label}
                </div>

                <div style={{ padding: '10px 14px', background: '#0f172a', borderRadius: '8px', marginTop: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Based on {fearGreedIndex.signalCount || 0} market signals</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                    Computed from RSI, MACD, and price momentum across monitored assets
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Overall Sentiment */}
        <div style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid #334155'
        }}>
          <h3 style={{ color: 'white', marginBottom: '20px', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BarChart2 size={20} color="#3b82f6" />
            {selectedSymbol} Sentiment
          </h3>

          {sentiment && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  border: `8px solid ${getSentimentColor(sentiment.overall)}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#0f172a'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: getSentimentColor(sentiment.overall) }}>
                    {(sentiment.overall * 100).toFixed(0)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Score</div>
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    background: sentiment.trend === 'bullish' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'
                  }}>
                    {sentiment.trend === 'bullish' ? (
                      <TrendingUp size={20} color="#10b981" />
                    ) : (
                      <TrendingDown size={20} color="#ef4444" />
                    )}
                    <span style={{
                      color: sentiment.trend === 'bullish' ? '#10b981' : '#ef4444',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {sentiment.trend}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    24h Change: <span style={{ color: sentiment.change_24h >= 0 ? '#10b981' : '#ef4444' }}>
                      {sentiment.change_24h >= 0 ? '+' : ''}{sentiment.change_24h}%
                    </span>
                  </div>
                </div>
              </div>

              <SentimentBar label="Bullish" value={sentiment.bullish} color="#10b981" />
              <SentimentBar label="Bearish" value={sentiment.bearish} color="#ef4444" />
              <SentimentBar label="Neutral" value={sentiment.neutral} color="#f59e0b" />
            </>
          )}
        </div>
      </div>

      {/* Signal Channels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        {/* Technical Signal Breakdown */}
        {socialMetrics?.signals && (
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #334155'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px', background: '#3b82f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <BarChart2 size={20} color="white" />
              </div>
              <div>
                <h4 style={{ color: 'white', margin: 0 }}>Technical Signals</h4>
                <div style={{ fontSize: '12px', color: '#64748b' }}>RSI + MACD + Price Action</div>
              </div>
              {socialMetrics.signals.strongSignal && (
                <span style={{
                  marginLeft: 'auto', padding: '4px 8px', borderRadius: '4px',
                  background: socialMetrics.signals.sentiment > 0.5 ? '#10b981' : '#ef4444',
                  color: 'white', fontSize: '10px', fontWeight: '600'
                }}>
                  STRONG
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Bullish</div>
                <div style={{ fontSize: '22px', fontWeight: '600', color: '#10b981' }}>{socialMetrics.signals.bullish}</div>
              </div>
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Bearish</div>
                <div style={{ fontSize: '22px', fontWeight: '600', color: '#ef4444' }}>{socialMetrics.signals.bearish}</div>
              </div>
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Neutral</div>
                <div style={{ fontSize: '22px', fontWeight: '600', color: '#f59e0b' }}>{socialMetrics.signals.neutral}</div>
              </div>
            </div>

            <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px' }}>Signal Consensus</div>
              <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                {socialMetrics.signals.count > 0 && (
                  <>
                    <div style={{ width: `${(socialMetrics.signals.bullish / socialMetrics.signals.count) * 100}%`, background: '#10b981' }} />
                    <div style={{ width: `${(socialMetrics.signals.neutral / socialMetrics.signals.count) * 100}%`, background: '#f59e0b' }} />
                    <div style={{ width: `${(socialMetrics.signals.bearish / socialMetrics.signals.count) * 100}%`, background: '#ef4444' }} />
                  </>
                )}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                {socialMetrics.signals.count} signals analyzed from {selectedSymbol} and correlated assets
              </div>
            </div>
          </div>
        )}

        {/* Political / White House Sentiment */}
        {politicalSentiment && (
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #334155'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px', background: '#7c3aed',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Globe size={20} color="white" />
              </div>
              <div>
                <h4 style={{ color: 'white', margin: 0 }}>Political Sentiment</h4>
                <div style={{ fontSize: '12px', color: '#64748b' }}>White House & Policy Impact</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '16px' }}>
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Avg Sentiment</div>
                <div style={{
                  fontSize: '22px', fontWeight: '600',
                  color: politicalSentiment.avgScore > 0.1 ? '#10b981' : politicalSentiment.avgScore < -0.1 ? '#ef4444' : '#f59e0b'
                }}>
                  {politicalSentiment.avgScore > 0 ? '+' : ''}{(politicalSentiment.avgScore * 100).toFixed(0)}
                </div>
              </div>
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Headlines (7d)</div>
                <div style={{ fontSize: '22px', fontWeight: '600', color: 'white' }}>{politicalSentiment.totalHeadlines}</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {politicalSentiment.trumpRelated > 0 && (
                <span style={{ background: '#7c3aed30', color: '#a855f7', padding: '3px 8px', borderRadius: '4px', fontSize: '11px' }}>
                  Trump: {politicalSentiment.trumpRelated}
                </span>
              )}
              {politicalSentiment.tariffRelated > 0 && (
                <span style={{ background: '#f59e0b30', color: '#f59e0b', padding: '3px 8px', borderRadius: '4px', fontSize: '11px' }}>
                  Tariff: {politicalSentiment.tariffRelated}
                </span>
              )}
              {politicalSentiment.fedRelated > 0 && (
                <span style={{ background: '#3b82f630', color: '#3b82f6', padding: '3px 8px', borderRadius: '4px', fontSize: '11px' }}>
                  Fed: {politicalSentiment.fedRelated}
                </span>
              )}
            </div>

            {politicalSentiment.news.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {politicalSentiment.news.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: '#0f172a', borderRadius: '6px' }}>
                    <span style={{
                      width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
                      background: (item.sentiment_score || 0) > 0.1 ? '#10b981' : (item.sentiment_score || 0) < -0.1 ? '#ef4444' : '#f59e0b'
                    }} />
                    <span style={{ color: '#d1d5db', fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.headline}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Additional Sentiment Channels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        {/* VIX Options Sentiment Proxy */}
        {vixData && (
          <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', padding: '24px', border: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Activity size={20} color="white" />
              </div>
              <div>
                <h4 style={{ color: 'white', margin: 0 }}>Market Options Sentiment</h4>
                <div style={{ fontSize: '12px', color: '#64748b' }}>VIX Volatility Index Proxy</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Current VIX</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: vixData.current > 25 ? '#ef4444' : vixData.current > 18 ? '#f59e0b' : '#10b981' }}>
                  {Number(vixData.current).toFixed(1)}
                </div>
              </div>
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Change</div>
                <div style={{ fontSize: '22px', fontWeight: '600', color: vixData.change > 0 ? '#ef4444' : '#10b981' }}>
                  {vixData.change > 0 ? '+' : ''}{Number(vixData.change).toFixed(2)}
                </div>
              </div>
            </div>
            {vixData.trend && vixData.trend.length > 2 && (
              <div style={{ marginBottom: '12px' }}>
                <svg width="100%" height="40" viewBox={`0 0 ${vixData.trend.length * 10} 40`} preserveAspectRatio="none">
                  <polyline
                    points={vixData.trend.map((v, i) => {
                      const min = Math.min(...vixData.trend);
                      const max = Math.max(...vixData.trend);
                      const range = max - min || 1;
                      return `${i * 10},${38 - ((v - min) / range) * 36}`;
                    }).join(' ')}
                    fill="none" stroke={vixData.current > 25 ? '#ef4444' : '#f59e0b'} strokeWidth="2"
                  />
                </svg>
              </div>
            )}
            <div style={{ padding: '8px 12px', background: '#0f172a', borderRadius: '6px', fontSize: '12px', color: '#94a3b8' }}>
              {vixData.interpretation}
            </div>
          </div>
        )}

        {/* Tariff Monitor */}
        {tariffMonitor && (
          <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', padding: '24px', border: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertTriangle size={20} color="white" />
              </div>
              <div>
                <h4 style={{ color: 'white', margin: 0 }}>Tariff Monitor</h4>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Trade Policy & Tariff Tracking (30d)</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Tariff Headlines</div>
                <div style={{ fontSize: '22px', fontWeight: '600', color: '#f59e0b' }}>{tariffMonitor.count}</div>
              </div>
              <div style={{ padding: '12px', background: '#0f172a', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Avg Sentiment</div>
                <div style={{ fontSize: '22px', fontWeight: '600', color: tariffMonitor.avgSentiment > 0 ? '#10b981' : '#ef4444' }}>
                  {tariffMonitor.avgSentiment > 0 ? '+' : ''}{(tariffMonitor.avgSentiment * 100).toFixed(0)}
                </div>
              </div>
            </div>
            {tariffMonitor.affectedSectors.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px' }}>Affected Sectors</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {tariffMonitor.affectedSectors.map(s => (
                    <span key={s.sector || s.name} style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, background: '#f59e0b20', color: '#f59e0b' }}>
                      {s.sector || s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {tariffMonitor.recentHeadlines.map((h, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: '#0f172a', borderRadius: '6px', marginBottom: '4px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: (h.sentiment_score || 0) > 0 ? '#10b981' : '#ef4444' }} />
                <span style={{ color: '#d1d5db', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {h.headline}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Earnings Channel - from news data */}
        {newsSentiment && newsSentiment.topStories && (
          <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', padding: '24px', border: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Zap size={20} color="white" />
              </div>
              <div>
                <h4 style={{ color: 'white', margin: 0 }}>Earnings & Guidance</h4>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Earnings-related news for {selectedSymbol}</div>
              </div>
            </div>
            {(() => {
              const earningsKeywords = /earn|eps|revenue|guidance|beat|miss|quarter|q[1-4]|profit|margin|dividend/i;
              const earningsStories = newsSentiment.topStories.filter(s => earningsKeywords.test(s.title));
              if (earningsStories.length === 0) {
                return (
                  <div style={{ padding: '16px', background: '#0f172a', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ color: '#64748b', fontSize: '13px' }}>No recent earnings news for {selectedSymbol}</div>
                    <div style={{ color: '#475569', fontSize: '11px', marginTop: '4px' }}>Earnings data appears before/after quarterly reports</div>
                  </div>
                );
              }
              return earningsStories.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: '#0f172a', borderRadius: '8px', marginBottom: '6px', borderLeft: `3px solid ${s.sentiment === 'positive' ? '#10b981' : s.sentiment === 'negative' ? '#ef4444' : '#f59e0b'}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#d1d5db', fontSize: '13px' }}>{s.title}</div>
                    <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>{s.source} — {s.time}</div>
                  </div>
                  <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                    background: s.sentiment === 'positive' ? '#10b98120' : s.sentiment === 'negative' ? '#ef444420' : '#f59e0b20',
                    color: s.sentiment === 'positive' ? '#10b981' : s.sentiment === 'negative' ? '#ef4444' : '#f59e0b' }}>
                    {s.sentiment}
                  </span>
                </div>
              ));
            })()}
          </div>
        )}
      </div>

      {/* News Sentiment */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid #334155'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'white', margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Newspaper size={20} color="#f59e0b" />
            News Sentiment
          </h3>
          {newsSentiment && (
            <div style={{ display: 'flex', gap: '16px' }}>
              <span style={{ color: '#10b981', fontSize: '13px' }}>
                <span style={{ fontWeight: '600' }}>{newsSentiment.positive}</span> Positive
              </span>
              <span style={{ color: '#f59e0b', fontSize: '13px' }}>
                <span style={{ fontWeight: '600' }}>{newsSentiment.neutral}</span> Neutral
              </span>
              <span style={{ color: '#ef4444', fontSize: '13px' }}>
                <span style={{ fontWeight: '600' }}>{newsSentiment.negative}</span> Negative
              </span>
            </div>
          )}
        </div>

        {newsSentiment?.topStories && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {newsSentiment.topStories.map((story, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  background: '#0f172a',
                  borderRadius: '10px',
                  borderLeft: `3px solid ${
                    story.sentiment === 'positive' ? '#10b981' :
                    story.sentiment === 'negative' ? '#ef4444' : '#f59e0b'
                  }`
                }}
              >
                <div>
                  <div style={{ color: 'white', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                    {story.title}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ color: '#64748b', fontSize: '12px' }}>{story.source}</span>
                    <span style={{ color: '#64748b', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Clock size={12} /> {story.time}
                    </span>
                  </div>
                </div>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  background: story.sentiment === 'positive' ? 'rgba(16, 185, 129, 0.2)' :
                              story.sentiment === 'negative' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                  color: story.sentiment === 'positive' ? '#10b981' :
                         story.sentiment === 'negative' ? '#ef4444' : '#f59e0b',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {story.sentiment}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Last Update */}
      {lastUpdate && (
        <div style={{ textAlign: 'center', marginTop: '20px', color: '#64748b', fontSize: '12px' }}>
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
